import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// In-memory token cache. Set by AuthContext on login/register, cleared on logout.
// Persisted across navigation; lost on page refresh (cookie takes over in that case).
let inMemoryToken = null;

export function setInMemoryToken(token) {
  inMemoryToken = token || null;
}

export function getInMemoryToken() {
  return inMemoryToken;
}

// withCredentials sends the httpOnly auth cookie on every request automatically.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// iter176 — Global 401 handling. When any authenticated call comes back
// with 401 (cookie expired, token revoked, etc.), we can't rely on every
// caller to redirect the user. Instead, AuthContext registers a handler
// here on mount that: clears the local user state, shows a "Session
// expired" toast, and navigates to the correct login surface (branded
// `/c/[slug]/login` when the user is inside a client env, otherwise
// `/login`). Set by AuthContext via `setSessionExpiredHandler`.
let sessionExpiredHandler = null;
let handlingSessionExpiry = false;
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = typeof fn === "function" ? fn : null;
}

// Defensive Bearer fallback: if a token was issued during this session, attach it.
// The backend's get_current_user reads the cookie first, then falls back to this header.
// This protects against browsers / contexts where the cookie isn't sent for some reason.
api.interceptors.request.use((config) => {
  if (inMemoryToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${inMemoryToken}`;
  }
  // Slug-scope injection: when the user is inside `/c/[slug]/*` we append
  // `?as_pub=<slug>` to every backend call. Super-Admin (`role="admin"`) uses
  // it to see that pub's leaderboard / predictions / leagues (backend
  // rewrites `user.pub_id` in `get_current_user`). Non-admins get their own
  // pub scope from the JWT — `as_pub` is silently ignored so bookmark links
  // remain safe.
  try {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const m = path.match(/^\/c\/([^/]+)(?:\/|$)/);
    if (m) {
      const slug = m[1];
      // Never inject on the auth handshake / public brand lookups.
      const url = config.url || "";
      const skip =
        url.startsWith("/auth/") ||
        url.startsWith("/pubs/by-slug/") ||
        url.startsWith("/business-demo/");
      // Preserve any as_pub the caller set explicitly.
      const hasParam = (config.params && config.params.as_pub) ||
                       (typeof url === "string" && url.includes("as_pub="));
      if (!skip && !hasParam) {
        config.params = { ...(config.params || {}), as_pub: slug };
      }
    }
  } catch (_e) {
    // Never let interceptor logic break a request.
  }
  return config;
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// iter176 — Response interceptor. Any 401 on an authenticated route
// fires the session-expired handler registered by AuthContext. We
// explicitly SKIP the auth handshake paths so a bad-password 401 on
// /auth/login doesn't kick the user into a redirect loop, and so
// /auth/me during bootstrap simply resolves to "not signed in" (the
// AuthProvider already handles that shape). Everything else — the
// live-score import, add-match, admin blast, etc. — now surfaces a
// friendly "Session expired" flow instead of a raw
// `Not authenticated` toast the user can't act on.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthHandshake =
      url.startsWith("/auth/login") ||
      url.startsWith("/auth/register") ||
      url.startsWith("/auth/logout") ||
      url.startsWith("/auth/me") ||
      url.startsWith("/auth/reset") ||
      url.startsWith("/auth/confirm-age");
    if (status === 401 && !isAuthHandshake && sessionExpiredHandler && !handlingSessionExpiry) {
      handlingSessionExpiry = true;
      try {
        sessionExpiredHandler();
      } finally {
        // Reset shortly so a fresh session hitting a stale 401 later still
        // triggers the flow, but a burst of parallel 401s during teardown
        // doesn't fan out to N redirects.
        setTimeout(() => {
          handlingSessionExpiry = false;
        }, 1500);
      }
    }
    return Promise.reject(err);
  },
);
